import { useMemo } from 'react';
import type { AvailableProjectParameterDescriptor } from '../../../shared/lib/revit/projectParameterMutationPlan';
import {
  assessProjectParameterRequirements,
  formatMappingParameterConflictMessage,
  reconcileScopedMissingParameters,
  selectUnsatisfiedRepairableRequirements,
  type MappingParameterAssessment,
  type MappingParameterScopeRequirement,
} from '../lib/mappingReadinessFamilyExclusions';
import type { MappingProjectReadinessDiff } from '../lib/mappingProjectReadiness';

export function useMappingScopedRequirementAssessment(input: {
  requirements: MappingParameterScopeRequirement[];
  baseDiff: MappingProjectReadinessDiff;
  projectDescriptors: AvailableProjectParameterDescriptor[];
  sampledParameterNames: string[];
  familyAssessments: MappingParameterAssessment[];
  familySatisfiedNames: string[];
}) {
  const projectAssessments = useMemo(
    () => assessProjectParameterRequirements(
      input.requirements,
      input.projectDescriptors,
      input.sampledParameterNames
    ),
    [input.projectDescriptors, input.requirements, input.sampledParameterNames]
  );
  const assessments = useMemo(
    () => [...projectAssessments, ...input.familyAssessments],
    [input.familyAssessments, projectAssessments]
  );
  const conflictMessage = useMemo(
    () => formatMappingParameterConflictMessage(input.requirements, assessments),
    [assessments, input.requirements]
  );
  const liveDiff = useMemo(
    () => reconcileScopedMissingParameters(
      input.baseDiff,
      input.requirements,
      assessments,
      {
        preserveCatalogSatisfiedRepairKinds: ['connector'],
        preserveCatalogSatisfiedParameterNames: input.sampledParameterNames,
      }
    ),
    [assessments, input.baseDiff, input.requirements, input.sampledParameterNames]
  );
  const unsatisfiedRepairableRequirements = useMemo(
    () => selectUnsatisfiedRepairableRequirements(input.requirements, assessments),
    [assessments, input.requirements]
  );
  return {
    assessments,
    conflictMessage,
    familySatisfiedNames: input.familySatisfiedNames,
    liveDiff,
    projectAssessments,
    unsatisfiedRepairableRequirements,
  };
}
